/**
 * Distance vs Time Chart
 *
 * Renders a uPlot chart showing cumulative distance (Y) against elapsed time (X).
 * Monotonically increasing — slope changes reveal pace variations.
 */

import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import uPlot from 'uplot'
import uPlotCss from 'uplot/dist/uPlot.min.css?inline'
import type { UnitSystem } from '../../utils/units.js'
import type { RestPeriod } from './elevation-profile.js'

export interface DistanceTimePoint {
  /** Elapsed time from recording start, in seconds */
  elapsed: number
  /** Cumulative distance from start, in meters */
  distance: number
}

@customElement('distance-time-chart')
export class DistanceTimeChart extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .chart-container {
      width: 100%;
      height: 100%;
    }

    .no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      opacity: 0.5;
      font-size: 13px;
    }

    .cursor-tooltip {
      position: absolute;
      top: 4px;
      right: 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-size: 11px;
      padding: 2px 6px;
      border-radius: 3px;
      pointer-events: none;
      z-index: 10;
      white-space: nowrap;
    }
  `

  @property({ type: Array })
  points: DistanceTimePoint[] = []

  @property({ type: String })
  unitSystem: UnitSystem = 'imperial'

  /** Rest periods to shade on the chart (elapsed seconds) */
  @property({ type: Array })
  restPeriods: RestPeriod[] = []

  private chart: uPlot | undefined
  private chartEl: HTMLDivElement | undefined
  private uplotStylesInjected = false
  private _syncing = false
  private _syncingZoom = false
  private resizeObserver?: ResizeObserver
  private cursorTooltip = ''
  private tooltipEl: HTMLElement | undefined

  /** Reflect the cursor tooltip to the DOM imperatively, bypassing a Lit
   *  re-render on every mousemove (the per-cursor garbage saturated GC). */
  private syncTooltipDom(): void {
    this.tooltipEl ??=
      (this.shadowRoot?.querySelector('.cursor-tooltip') as HTMLElement | null) ?? undefined
    const el = this.tooltipEl
    if (!el) return
    el.textContent = this.cursorTooltip
    el.hidden = !this.cursorTooltip
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.chart?.destroy()
    this.chart = undefined
    this.resizeObserver?.disconnect()
  }

  override firstUpdated(): void {
    this.chartEl =
      (this.shadowRoot?.querySelector('.chart-container') as HTMLDivElement | null) ?? undefined
    if (!this.chartEl) return

    this.resizeObserver = new ResizeObserver(() => {
      if (this.chart && this.chartEl) {
        this.chart.setSize({
          width: this.chartEl.clientWidth,
          height: this.chartEl.clientHeight,
        })
      }
    })
    this.resizeObserver.observe(this.chartEl)

    this.rebuildChart()
  }

  override updated(changed: Map<string, unknown>): void {
    this.chartEl =
      (this.shadowRoot?.querySelector('.chart-container') as HTMLDivElement | null) ?? undefined

    if (changed.has('points') || changed.has('unitSystem') || changed.has('restPeriods')) {
      this.rebuildChart()
    }
  }

  private ensureStyles(): void {
    if (!this.uplotStylesInjected && this.shadowRoot) {
      const style = document.createElement('style')
      style.textContent =
        uPlotCss +
        `\n.u-cursor-x { border-left: 1.5px solid rgba(255,255,255,0.8) !important; }` +
        `\n.u-cursor-pt { display: none !important; }` +
        `\n.u-select { background: rgba(100,160,220,0.3) !important; }`
      this.shadowRoot.appendChild(style)
      this.uplotStylesInjected = true
    }
  }

  /** Linearly interpolate y at xVal in sorted parallel arrays xs, ys */
  private interpolateY(xs: number[], ys: number[], xVal: number): number | null {
    if (xs.length === 0) return null
    if (xVal <= xs[0]) return ys[0]
    if (xVal >= xs[xs.length - 1]) return ys[xs.length - 1]
    for (let i = 1; i < xs.length; i++) {
      if (xs[i] >= xVal) {
        const t = (xVal - xs[i - 1]) / (xs[i] - xs[i - 1])
        return ys[i - 1] + t * (ys[i] - ys[i - 1])
      }
    }
    return ys[xs.length - 1]
  }

  /** Programmatically position cursor at a given elapsed time (for cross-chart sync) */
  setCursorElapsed(elapsed: number | null): void {
    if (!this.chart) return
    this._syncing = true
    if (elapsed == null) {
      this.chart.setCursor({ left: -10, top: -10 })
    } else {
      const left = this.chart.valToPos(elapsed, 'x')
      this.chart.setCursor({ left, top: -10 })
    }
    this._syncing = false
  }

  /** Programmatically set the x-axis zoom range (for cross-chart zoom sync) */
  setZoomRange(min: number | null, max: number | null): void {
    if (!this.chart) return
    this._syncingZoom = true
    if (min == null || max == null) {
      this.chart.setScale('x', {
        min: this.chart.data[0][0],
        max: this.chart.data[0][this.chart.data[0].length - 1],
      })
    } else {
      this.chart.setScale('x', { min, max })
    }
    this._syncingZoom = false
  }

  private clearCursor(): void {
    this.cursorTooltip = ''
    this.syncTooltipDom()
    if (!this._syncing) {
      this.dispatchEvent(
        new CustomEvent('chart-cursor', {
          detail: { elapsed: null },
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  private formatElapsed(s: number): string {
    const m = Math.floor(s / 60)
    const h = Math.floor(m / 60)
    if (h > 0) return `${h}h${String(m % 60).padStart(2, '0')}m`
    return `${m}m${String(Math.floor(s) % 60).padStart(2, '0')}s`
  }

  private rebuildChart(): void {
    if (!this.chartEl) return

    this.chart?.destroy()
    this.chart = undefined

    if (this.points.length < 2) return

    this.ensureStyles()

    const isMetric = this.unitSystem === 'metric'
    const distFactor = isMetric ? 0.001 : 0.000621371
    const distUnit = isMetric ? 'km' : 'mi'

    const times = this.points.map(p => p.elapsed)
    const distances = this.points.map(p => p.distance * distFactor)

    const opts: uPlot.Options = {
      width: this.chartEl.clientWidth,
      height: this.chartEl.clientHeight,
      cursor: { show: true, x: true, y: false, drag: { x: true, y: false, setScale: true } },
      legend: { show: false },
      axes: [
        {
          stroke: 'white',
          grid: { stroke: 'rgba(255,255,255,0.3)', width: 1 },
          ticks: { stroke: 'rgba(255,255,255,0.5)', width: 1 },
          font: '10px sans-serif',
          space: 60,
          values: (_u: uPlot, vals: number[]) =>
            vals.map(v => {
              const m = Math.floor(v / 60)
              const h = Math.floor(m / 60)
              if (h > 0) return `${h}h${String(m % 60).padStart(2, '0')}m`
              return `${m}m`
            }),
        },
        {
          stroke: 'white',
          grid: { stroke: 'rgba(255,255,255,0.3)', width: 1 },
          ticks: { stroke: 'rgba(255,255,255,0.5)', width: 1 },
          font: '10px sans-serif',
          size: 55,
          space: 40,
          values: (_u: uPlot, vals: number[]) => vals.map(v => `${v.toFixed(1)}${distUnit}`),
        },
      ],
      scales: { x: { auto: true }, y: { auto: true } },
      series: [
        {},
        {
          label: 'Distance',
          stroke: '#22c55e',
          fill: 'rgba(34,197,94,0.1)',
          width: 2,
        },
      ],
      hooks: {
        setCursor: [
          (u: uPlot) => {
            const left = u.cursor.left
            if (left == null || left < 0) {
              this.clearCursor()
              return
            }
            const xVal = u.posToVal(left, 'x')
            // Interpolate distance at this elapsed time
            const distVal = this.interpolateY(times, distances, xVal)
            if (distVal == null) {
              this.clearCursor()
              return
            }
            this.cursorTooltip = `${this.formatElapsed(xVal)}  ${distVal.toFixed(2)}${distUnit}`
            this.syncTooltipDom()
            if (!this._syncing) {
              this.dispatchEvent(
                new CustomEvent('chart-cursor', {
                  detail: { elapsed: xVal },
                  bubbles: true,
                  composed: true,
                })
              )
            }
          },
        ],
        setScale: [
          (u: uPlot, key: string) => {
            if (key === 'x' && !this._syncingZoom) {
              this.dispatchEvent(
                new CustomEvent('chart-zoom', {
                  detail: { min: u.scales.x.min, max: u.scales.x.max },
                  bubbles: true,
                  composed: true,
                })
              )
            }
          },
        ],
        draw: [
          (u: uPlot) => {
            if (this.restPeriods.length === 0) return
            const ctx = u.ctx
            ctx.save()
            ctx.fillStyle = 'rgba(255,200,0,0.12)'
            for (const rp of this.restPeriods) {
              const x0 = u.valToPos(rp.start, 'x', true)
              const x1 = u.valToPos(rp.end, 'x', true)
              if (x1 < u.bbox.left || x0 > u.bbox.left + u.bbox.width) continue
              const left = Math.max(x0, u.bbox.left)
              const right = Math.min(x1, u.bbox.left + u.bbox.width)
              ctx.fillRect(left, u.bbox.top, right - left, u.bbox.height)
            }
            ctx.restore()
          },
        ],
      },
    }

    const data: uPlot.AlignedData = [times, distances]
    this.chart = new uPlot(opts, data, this.chartEl)
    this.setupTouchCursor()
  }

  /** Wire touch events to uPlot cursor and long-press drag-to-zoom */
  private setupTouchCursor(): void {
    if (!this.chart) return
    const chart = this.chart
    const el = chart.root.querySelector('.u-over')
    if (!el) return
    const wrap = el as HTMLElement

    const LONG_PRESS_MS = 400
    const MOVE_THRESHOLD = 8
    let pressTimer: ReturnType<typeof setTimeout> | null = null
    let isDragging = false
    let dragStartLeft = 0
    let startX = 0
    let startY = 0
    let lastTap = 0

    wrap.addEventListener(
      'touchstart',
      (e: TouchEvent) => {
        const touch = e.touches[0]
        const rect = wrap.getBoundingClientRect()
        startX = touch.clientX
        startY = touch.clientY
        const left = touch.clientX - rect.left
        const top = touch.clientY - rect.top

        const now = Date.now()
        if (now - lastTap < 300) {
          e.preventDefault()
          lastTap = 0
          chart.setScale('x', {
            min: chart.data[0][0],
            max: chart.data[0][chart.data[0].length - 1],
          })
          return
        }
        lastTap = now

        isDragging = false
        pressTimer = setTimeout(() => {
          isDragging = true
          dragStartLeft = left
          chart.setSelect({ left, width: 0, top: 0, height: chart.over.clientHeight }, false)
          try {
            const cap = (window as unknown as Record<string, unknown>).Capacitor as
              | { Plugins?: { Haptics?: { impact: (opts: { style: string }) => void } } }
              | undefined
            cap?.Plugins?.Haptics?.impact({ style: 'medium' })
          } catch {
            // Haptics not available on web
          }
        }, LONG_PRESS_MS)

        chart.setCursor({ left, top }, true)
      },
      { passive: false }
    )

    wrap.addEventListener(
      'touchmove',
      (e: TouchEvent) => {
        const touch = e.touches[0]
        const rect = wrap.getBoundingClientRect()
        const left = touch.clientX - rect.left
        const top = touch.clientY - rect.top

        if (
          pressTimer &&
          (Math.abs(touch.clientX - startX) > MOVE_THRESHOLD ||
            Math.abs(touch.clientY - startY) > MOVE_THRESHOLD)
        ) {
          clearTimeout(pressTimer)
          pressTimer = null
        }

        if (isDragging) {
          e.preventDefault()
          const selLeft = Math.min(dragStartLeft, left)
          const selWidth = Math.abs(left - dragStartLeft)
          chart.setSelect(
            { left: selLeft, width: selWidth, top: 0, height: chart.over.clientHeight },
            false
          )
        } else {
          e.preventDefault()
          chart.setCursor({ left, top }, true)
        }
      },
      { passive: false }
    )

    wrap.addEventListener('touchend', () => {
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
      if (isDragging) {
        const sel = chart.select
        if (sel.width > 10) {
          const xMin = chart.posToVal(sel.left, 'x')
          const xMax = chart.posToVal(sel.left + sel.width, 'x')
          chart.setScale('x', { min: xMin, max: xMax })
        }
        chart.setSelect({ left: 0, width: 0, top: 0, height: 0 }, false)
        isDragging = false
      }
      this.clearCursor()
    })
  }

  override render(): unknown {
    // Tooltip is always present (hidden when empty) and updated imperatively via
    // syncTooltipDom() — never through a per-mousemove render.
    return html`<div class="chart-container">
        ${this.points.length < 2 ? html`<div class="no-data">No distance data yet</div>` : ''}
      </div>
      <div class="cursor-tooltip" hidden></div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'distance-time-chart': DistanceTimeChart
  }
}
